import { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, CheckCircle, RefreshCw } from '../icons/wp-icons';
import apiClient from '../api/apiClient';
import { clearBrandingCache } from '../components/BrandingLoader';
import { clearAllCache } from '../utils/offlineDB';
import { getHostBranding } from '../utils/hostBranding';
import HostBrandedNotice from '../components/HostBrandedNotice';

function VerifyEmail() {
  const hostBranding = useMemo(() => getHostBranding(), []);
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [countdown, setCountdown] = useState(0);

  const inputRefs = useRef([]);

  // Theme-Color setzen (Whitelabel-Hosts übernehmen ihre eigene via HostBrandedNotice)
  useEffect(() => {
    if (hostBranding) return;
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const originalColor = metaThemeColor?.getAttribute('content');

    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', '#378ab2');
    }

    return () => {
      if (metaThemeColor && originalColor) {
        metaThemeColor.setAttribute('content', originalColor);
      }
    };
  }, [hostBranding]);

  // HeyKiki-Sub-Orgs werden mit `emailVerified=true` provisioniert — diese
  // Seite sollte nie regulär erreicht werden. Falls doch (Deep-Link o.ä.),
  // Whitelabel-Hinweis statt WerkPilot-Form.
  if (hostBranding?.passwordFlowHandledExternally) {
    return (
      <HostBrandedNotice
        hostBranding={hostBranding}
        pageTitle="E-Mail verifizieren"
        title="E-Mail-Verifizierung"
        message={`Dein Account wird direkt von ${hostBranding.supportContact} verwaltet — eine separate E-Mail-Verifizierung ist nicht nötig. Du kannst dich direkt einloggen.`}
      />
    );
  }

  // Redirect wenn keine Email vorhanden
  useEffect(() => {
    if (!email) {
      navigate('/login', { replace: true });
    }
  }, [email, navigate]);

  // Countdown Timer fuer erneutes Senden
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Auto-focus auf erstes Eingabefeld
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleCodeChange = (index, value) => {
    // Nur Zahlen erlauben
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus auf naechstes Feld
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit wenn alle Felder gefuellt
    if (value && index === 5 && newCode.every(c => c !== '')) {
      handleSubmit(null, newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace: Zum vorherigen Feld wechseln
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Pfeil links
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Pfeil rechts
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < 6; i++) {
        newCode[i] = pastedData[i] || '';
      }
      setCode(newCode);

      // Focus auf letztes gefuelltes oder naechstes leeres Feld
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();

      // Auto-submit wenn alle 6 Ziffern eingefuegt
      if (pastedData.length === 6) {
        handleSubmit(null, pastedData);
      }
    }
  };

  const handleSubmit = async (e, autoCode = null) => {
    if (e) e.preventDefault();

    const verificationCode = autoCode || code.join('');

    if (verificationCode.length !== 6) {
      setError('Bitte geben Sie den vollstaendigen 6-stelligen Code ein');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post('/auth/verify-email', {
        email,
        code: verificationCode
      });

      if (response.data.success && response.data.token) {
        // Caches loeschen und Token setzen
        clearBrandingCache();
        await clearAllCache();
        localStorage.setItem('token', response.data.token);

        setSuccess(true);

        // Nach kurzer Verzoegerung weiterleiten
        setTimeout(() => {
          // Pruefen ob Onboarding noetig
          if (response.data.user?.organisation && !response.data.user.organisation.onboardingCompleted) {
            window.location.href = '/onboarding';
          } else {
            window.location.href = '/dashboard';
          }
        }, 1500);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.';
      setError(errorMessage);
      // Code zuruecksetzen bei Fehler
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;

    setResending(true);
    setResendMessage('');
    setError('');

    try {
      await apiClient.post('/auth/resend-code', { email });
      setResendMessage('Neuer Code wurde gesendet!');
      setCountdown(60); // 60 Sekunden warten vor erneutem Senden
      // Code-Felder zuruecksetzen
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Code konnte nicht gesendet werden');
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>

          <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            E-Mail bestaetigt!
          </h2>

          <p className="text-text-muted mb-6">
            Ihre E-Mail-Adresse wurde erfolgreich verifiziert.
            Sie werden weitergeleitet...
          </p>

          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#378ab2] mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src="/icons/icon-512x512.png"
            alt="WerkPilot Logo"
            className="w-24 h-24 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            E-Mail bestaetigen
          </h1>
          <p className="text-text-muted mt-2">
            Wir haben einen 6-stelligen Code an
          </p>
          <p className="font-medium text-gray-900 mt-1">
            {email}
          </p>
          <p className="text-text-muted mt-1">
            gesendet.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {resendMessage && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
            {resendMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-3 text-center">
              Verifizierungscode eingeben
            </label>
            <div className="flex justify-center gap-2">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className="w-12 h-14 text-center text-2xl font-bold border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] focus:border-transparent disabled:opacity-50"
                  disabled={loading}
                />
              ))}
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-[#378ab2] text-white py-3 rounded-button font-medium hover:bg-[#2a6d8a] transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || code.some(c => c === '')}
          >
            {loading ? 'Wird geprueft...' : 'Bestaetigen'}
          </button>
        </form>

        {/* Code erneut senden */}
        <div className="mt-6 text-center">
          <p className="text-sm text-text-muted mb-2">
            Keinen Code erhalten?
          </p>
          <button
            onClick={handleResendCode}
            disabled={resending || countdown > 0}
            className="inline-flex items-center gap-2 text-[#378ab2] hover:underline font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
          >
            <RefreshCw className={`w-4 h-4 ${resending ? 'animate-spin' : ''}`} />
            {countdown > 0
              ? `Erneut senden (${countdown}s)`
              : resending
                ? 'Wird gesendet...'
                : 'Code erneut senden'}
          </button>
        </div>

        {/* Hinweis */}
        <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800 text-center">
            Der Code ist 15 Minuten gueltig. Pruefen Sie auch Ihren Spam-Ordner.
          </p>
        </div>

        <div className="mt-6 pt-6 border-t border-border text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#378ab2] hover:underline font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurueck zur Anmeldung
          </Link>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
